document.addEventListener('click', async (event) => {
  const trigger = event.target.closest('[data-cookie-preferences]');

  if (!trigger) {
    return;
  }

  event.preventDefault();

  if (window.privacyBanner?.showPreferences) {
    await window.privacyBanner.showPreferences();
  }
});

const footerMobileMedia = window.matchMedia('(max-width: 699px)');

function syncFooterMenuState(root = document) {
  root.querySelectorAll('.footer__menu-details').forEach((details) => {
    if (footerMobileMedia.matches) {
      details.removeAttribute('open');
    } else {
      details.setAttribute('open', '');
    }
  });
}

syncFooterMenuState();

footerMobileMedia.addEventListener('change', () => syncFooterMenuState());

document.addEventListener('shopify:section:load', (event) => {
  syncFooterMenuState(event.target);
});

// Shopify can lose one of two cart changes that arrive at the same time (two quick add or "Verwijderen" clicks):
// queue add/change/update/clear requests so each one starts after the previous one has been answered.
const cartMutationPath = /\/cart\/(add|change|update|clear)(\.js)?$/;
const nativeFetch = window.fetch.bind(window);
let cartMutationQueue = Promise.resolve();

window.fetch = (resource, options) => {
  const url = new URL(resource instanceof Request ? resource.url : String(resource), window.location.origin);

  if (!cartMutationPath.test(url.pathname)) {
    return nativeFetch(resource, options);
  }

  const request = cartMutationQueue.then(() => nativeFetch(resource, options));
  cartMutationQueue = request.catch(() => {});

  return request;
};

// Impact shows a refused add (for example when the maximum stock is already in the cart) only next to the buy buttons
// of a product page. Quick add on a product card gave no visible feedback, so show Shopify's message as a short toast.
let cartErrorToast;
let cartErrorToastTimer;

function removeCartErrorToast() {
  clearTimeout(cartErrorToastTimer);
  cartErrorToast?.remove();
  cartErrorToast = null;
}

function scheduleCartErrorToastRemoval() {
  clearTimeout(cartErrorToastTimer);
  cartErrorToastTimer = setTimeout(removeCartErrorToast, 7000);
}

document.addEventListener('cart:error', (event) => {
  const form = event.target;
  const formId = form instanceof HTMLFormElement ? form.getAttribute('id') : null;
  const message = event.detail?.error;

  if (!formId || !message || document.querySelector(`buy-buttons[form="${CSS.escape(formId)}"]`)) {
    return;
  }

  const strings = window.themeVariables?.strings || {};
  const productTitle = form
    .closest('.product-card, .horizontal-product')
    ?.querySelector('.product-card__title, .horizontal-product__info .bold')
    ?.textContent.trim();

  removeCartErrorToast();

  // A new role="alert" element is announced reliably by screen readers; the text is set with textContent only.
  cartErrorToast = document.createElement('div');
  cartErrorToast.className = 'envee-cart-toast';
  cartErrorToast.setAttribute('role', 'alert');
  cartErrorToast.innerHTML = `
    <svg class="envee-cart-toast__icon" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
      <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
      <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
    </svg>
    <div class="envee-cart-toast__text">
      <p class="envee-cart-toast__title"></p>
      <p class="envee-cart-toast__message"></p>
      <a class="envee-cart-toast__link" href="${window.Shopify?.routes?.root || '/'}cart"></a>
    </div>
    <button type="button" class="envee-cart-toast__close">
      <svg viewBox="0 0 14 14" aria-hidden="true" focusable="false"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"></path></svg>
    </button>
  `;

  const title = cartErrorToast.querySelector('.envee-cart-toast__title');
  const link = cartErrorToast.querySelector('.envee-cart-toast__link');
  const close = cartErrorToast.querySelector('.envee-cart-toast__close');

  if (productTitle) {
    title.textContent = productTitle;
  } else {
    title.remove();
  }

  cartErrorToast.querySelector('.envee-cart-toast__message').textContent = message;
  link.textContent = strings.viewCart || 'Winkelwagen bekijken';
  close.setAttribute('aria-label', strings.accessibilityClose || 'Sluiten');

  link.addEventListener('click', (clickEvent) => {
    const cartDrawer = document.getElementById('cart-drawer');

    if (typeof cartDrawer?.show === 'function') {
      clickEvent.preventDefault();
      removeCartErrorToast();
      cartDrawer.show();
    }
  });

  close.addEventListener('click', removeCartErrorToast);

  // Keep the toast while the visitor points at or tabs into it.
  cartErrorToast.addEventListener('pointerenter', () => clearTimeout(cartErrorToastTimer));
  cartErrorToast.addEventListener('pointerleave', scheduleCartErrorToastRemoval);
  cartErrorToast.addEventListener('focusin', () => clearTimeout(cartErrorToastTimer));
  cartErrorToast.addEventListener('focusout', scheduleCartErrorToastRemoval);

  document.body.append(cartErrorToast);
  requestAnimationFrame(() => cartErrorToast?.classList.add('is-visible'));
  scheduleCartErrorToastRemoval();
});

// Back/Forward can rebuild a page from the browser's HTTP cache instead of the back/forward cache. Impact only
// refreshes the cart for the latter, so the cart drawer kept older items and an older total than the cart itself.
// Impact's own refresh (cart:refresh) does not help here: while the page loads from cache, its request for the
// drawer is answered from that cache too. So fetch past the cache and swap the drawer in the same way Impact does.
async function refreshCartFromServer() {
  const root = window.Shopify?.routes?.root || '/';
  const drawer = document.querySelector('cart-drawer');
  const sectionId = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
  const cart = await (await fetch(`${root}cart.js`, { cache: 'no-store' })).json();

  document.querySelectorAll('cart-count').forEach((count) => {
    count.itemCount = cart.item_count;
  });

  if (drawer && sectionId) {
    const container = document.createElement('div');
    container.innerHTML = await (await fetch(`${root}?section_id=${sectionId}`, { cache: 'no-store' })).text();

    const freshDrawer = container.querySelector('#cart-drawer');

    if (freshDrawer) {
      drawer.replaceChildren(...freshDrawer.children);
    }
  }
}

window.addEventListener('pageshow', (event) => {
  if (event.persisted || performance.getEntriesByType('navigation')[0]?.type !== 'back_forward') {
    return;
  }

  if (window.themeVariables?.settings?.pageType === 'cart') {
    window.location.reload();
  } else {
    refreshCartFromServer().catch(() => {});
  }
});
